import { Capacitor } from '@capacitor/core';

/**
 * 原生能力适配层。
 *
 * 同一套代码跑浏览器和 App —— 有原生插件就用,没有就回落 Web API。
 * 业务代码只调这里的函数,不直接 import 插件。
 */

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform() as 'web' | 'ios' | 'android';

export interface Position {
  lat: number;
  lng: number;
  accuracyM?: number;
  speedMps?: number;
  heading?: number;
}

/**
 * 取当前定位。
 *
 * 拿不到返回 null 而不是抛错 —— 定位失败不该阻塞司机提交操作,
 * 围栏校验由后端决定要不要拦。
 */
export async function getPosition(
  timeoutMs = 5000,
  /**
   * 最多接受多旧的缓存位置。默认 30 秒 —— 上报轨迹、规划路线时够用,还省电。
   * 「把这里存成终点」要的是**此刻**站的地方,传 0:半分钟前在路上的那个点存成「家」就错了。
   */
  maxAgeMs = 30_000,
): Promise<Position | null> {
  if (isNative) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      /*
       * ★ 插件自己的 timeout 不可信,外面再套一层。
       *   系统拒绝定位(比如 Info.plist 缺权限说明)时,插件的请求**永远不返回** ——
       *   不报错也不超时,取货、送达就一直卡在「处理中」,司机只能杀掉 App。
       *   多给 1 秒余量,正常情况下总是插件自己先回来。
       */
      const p = await Promise.race([
        Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: timeoutMs,
          maximumAge: maxAgeMs,
        }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('定位超时')), timeoutMs + 1000),
        ),
      ]);
      return {
        lat: p.coords.latitude,
        lng: p.coords.longitude,
        accuracyM: Math.round(p.coords.accuracy),
        /*
         * ★ iOS 用 -1 表示「不知道」(静止、刚定位、模拟器里一直是 -1),不是 null。
         *   原样传上去会被服务端按「速度不能为负」整批拒掉 ——
         *   位置上报就是这么一个点都没传上去的。
         */
        ...(p.coords.speed != null && p.coords.speed >= 0 ? { speedMps: p.coords.speed } : {}),
        ...(p.coords.heading != null && p.coords.heading >= 0
          ? { heading: Math.round(p.coords.heading) % 360 }
          : {}),
      };
    } catch {
      return null;
    }
  }

  if (!navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracyM: Math.round(p.coords.accuracy),
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: maxAgeMs },
    );
  });
}

/** 请求定位权限。返回是否已授予 —— 未授予时 UI 要给出引导 */
export async function ensureLocationPermission(): Promise<boolean> {
  if (!isNative) return true; // 浏览器在首次调用时自动弹窗
  try {
    const { Geolocation } = await import('@capacitor/geolocation');
    const status = await Geolocation.checkPermissions();
    if (status.location === 'granted') return true;
    const asked = await Geolocation.requestPermissions();
    return asked.location === 'granted';
  } catch {
    return false;
  }
}

/**
 * 拍照。
 *
 * 原生用相机插件(体验更好、能直接拿 base64),
 * Web 回落到 <input capture> —— 由调用方处理。
 */
export async function takePhoto(): Promise<Blob | null> {
  return getImage('CAMERA');
}

/**
 * 从相册里选一张。
 *
 * ★ 为什么要有这个:司机在门口先拍了照,然后手机进了口袋、
 *   点确认时才发现刚才那张没存上;或者货是同事拍的、发在群里的。
 *   只给「拍照」一条路的话,这两种情况他就卡在那儿了 ——
 *   而卡住的后果是这一单迟迟不能确认。
 */
export async function pickFromGallery(): Promise<Blob | null> {
  return getImage('PHOTOS');
}

async function getImage(source: 'CAMERA' | 'PHOTOS'): Promise<Blob | null> {
  if (!isNative) return null; // Web 走 input 元素

  try {
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    const photo = await Camera.getPhoto({
      quality: 85,
      allowEditing: false,
      resultType: CameraResultType.Uri,
      source: source === 'CAMERA' ? CameraSource.Camera : CameraSource.Photos,
      // 直接开对应的来源,不再弹一层「拍照/相册」让他选
      saveToGallery: false,
    });
    if (!photo.webPath) return null;
    const res = await fetch(photo.webPath);
    return await res.blob();
  } catch {
    return null; // 用户取消也走这里
  }
}

/** 震动反馈。操作被接收的即时确认,戴手套时尤其有用 */
export async function haptic(style: 'light' | 'medium' | 'heavy' = 'medium'): Promise<void> {
  if (isNative) {
    try {
      const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
      const map = {
        light: ImpactStyle.Light,
        medium: ImpactStyle.Medium,
        heavy: ImpactStyle.Heavy,
      };
      await Haptics.impact({ style: map[style] });
      return;
    } catch {
      /* 回落 */
    }
  }
  navigator.vibrate?.(style === 'heavy' ? 60 : style === 'medium' ? 30 : 15);
}

/**
 * 网络状态。
 *
 * navigator.onLine 在移动端不可靠(连上 WiFi 但没有外网也报 true),
 * 原生插件的判断准确得多 —— 这直接影响离线队列什么时候尝试同步。
 */
export async function watchNetwork(cb: (online: boolean) => void): Promise<() => void> {
  if (isNative) {
    try {
      const { Network } = await import('@capacitor/network');
      const status = await Network.getStatus();
      cb(status.connected);
      const handle = await Network.addListener('networkStatusChange', (s) => cb(s.connected));
      return () => void handle.remove();
    } catch {
      /* 回落 */
    }
  }

  const on = () => cb(true);
  const off = () => cb(false);
  cb(navigator.onLine);
  window.addEventListener('online', on);
  window.addEventListener('offline', off);
  return () => {
    window.removeEventListener('online', on);
    window.removeEventListener('offline', off);
  };
}

/** App 前后台切换。回到前台时要立刻同步队列 */
export async function onAppResume(cb: () => void): Promise<() => void> {
  if (isNative) {
    try {
      const { App } = await import('@capacitor/app');
      const handle = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) cb();
      });
      return () => void handle.remove();
    } catch {
      /* 回落 */
    }
  }

  const onVis = () => {
    if (document.visibilityState === 'visible') cb();
  };
  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}

/**
 * 热更新插件。Web 端没有「装在本地的网页包」这回事,返回 null。
 *
 * ★ 更新策略不写在这里(见 lib/liveUpdate.ts)—— 这里只负责把插件交出去,
 *   和其他原生能力一样,插件只在这个文件里 import。
 *
 * ★ 要包一层对象再返回,不能直接 `return CapacitorUpdater`。
 *   Capacitor 的插件是个 Proxy,**任何属性名**都被当成原生方法 —— 包括 `then`。
 *   async 函数直接返回它,`await` 会把它当 Promise 去调 `.then()`,
 *   而那个「原生方法」永远不回 —— 整个热更新卡死在这一行,不报错。
 *   (模拟器上第一次跑就是这样:插件加载了,notifyAppReady 一次都没调到。)
 */
export async function loadUpdater() {
  if (!isNative) return null;
  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    return { updater: CapacitorUpdater };
  } catch {
    return null;
  }
}

/** 原生壳的版本号(iOS 的 CFBundleShortVersionString)。Web 返回 null */
export async function nativeAppVersion(): Promise<string | null> {
  if (!isNative) return null;
  try {
    const { App } = await import('@capacitor/app');
    return (await App.getInfo()).version;
  } catch {
    return null;
  }
}

/**
 * 唤起外部 URL(导航 deep link)。
 *
 * 用 window.open('_system') 而不是 location.href —— 后者在 WebView 里
 * 会把导航网页装进 App 内,司机就退不回来了。
 * scheme(comgooglemaps:// 等)本来也该交给系统处理。
 */
/**
 * 打开外部链接(导航 App、网页版地图)。
 *
 * ★ 网页端原来用的是 `location.href = url` —— 那是**跳走当前页面**。
 *   司机点一下导航,司机端就被顶掉了:回来要重新加载,
 *   刚才在哪一单、拍了几张照全没了。而他在送货途中会反复看导航。
 *
 * ★ 新窗口打开。被拦截时(不是用户手势触发的那种)再回落到当前页 ——
 *   跳走总比什么都不发生强,后者会让他以为按钮坏了。
 */
export async function openExternal(url: string): Promise<void> {
  if (isNative) {
    window.open(url, '_system');
    return;
  }
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (!w) window.location.href = url;
}

/**
 * 本地通知 —— 「来新单了」。
 *
 * ★ 这不是服务端推送。真正的推送(FCM / APNs)要凭证和原生打包,
 *   目前 `pushNotifyDriver` 是空实现,只往 notification_log 里落一条。
 *   在那之前,司机把 app 切到后台、屏幕一黑,就**完全不知道来了新单** ——
 *   只能靠他自己隔一会儿打开看一眼。
 *
 *   本地通知覆盖的是「app 还开着、只是没在前台」这一大半场景:
 *   页面照常轮询,发现新单就弹一条系统通知 + 震一下。
 *   app 被彻底杀掉的那部分,只有真推送能解决 —— 这里不假装能。
 *
 * ★ 不主动要权限。第一次打开就弹「允许通知吗」是最招人烦的做法,
 *   而且被拒之后再也要不回来。由调用方在合适的时机(司机上线时)去要。
 */
export async function notify(title: string, body: string): Promise<void> {
  if (isNative) {
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      await LocalNotifications.schedule({
        notifications: [{ id: Date.now() % 2_147_483_647, title, body }],
      });
      return;
    } catch {
      /* 回落到浏览器通知 */
    }
  }
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification(title, { body });
    }
  } catch {
    /* 有些内嵌浏览器里 Notification 构造会直接抛 —— 不能让它把轮询打断 */
  }
}

/**
 * 要通知权限。
 *
 * ★ 返回「现在到底能不能弹」,而不是「用户点了什么」——
 *   调用方要的是前者。已经拒过的情况下再调也不会再弹窗,
 *   浏览器直接返回 denied,所以反复调是安全的。
 */
export async function ensureNotifyPermission(): Promise<boolean> {
  if (isNative) {
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const r = await LocalNotifications.requestPermissions();
      return r.display === 'granted';
    } catch {
      return false;
    }
  }
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    return (await Notification.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}
